import React, { useState, useEffect, useRef } from 'react';
import WebcamFeed from '../components/WebcamFeed';
import GameHUD from '../components/GameHUD';
import FeedbackOverlay from '../components/FeedbackOverlay';
import { detectWave } from '../utils/poseCalculations';
import { Camera, User } from 'lucide-react';

const MirrorGame = ({ onGameEnd }) => {
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const [accuracy, setAccuracy] = useState(100);
  const [timer, setTimer] = useState(60);
  const [restTime, setRestTime] = useState(20);
  const [isResting, setIsResting] = useState(true);
  const [round, setRound] = useState(1);
  const [feedback, setFeedback] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [revealed, setRevealed] = useState(0);
  const [viewMode, setViewMode] = useState('camera');
  const previousPoseRef = useRef(null);
  const lastWaveTimeRef = useRef(0);
  const totalMovesRef = useRef(0);
  const correctMovesRef = useRef(0);
  const audioContextRef = useRef(null);
  const musicAudioRef = useRef(null);
  const scoreRef = useRef(score);
  const accuracyRef = useRef(accuracy);
  const onGameEndRef = useRef(onGameEnd);
  scoreRef.current = score;
  accuracyRef.current = accuracy;
  onGameEndRef.current = onGameEnd;

  useEffect(() => {
    // Start music once on first GET READY countdown
    if (isResting && round === 1) {
      playBackgroundMusic();
    }

    if (isResting) {
      const restInterval = setInterval(() => {
        setRestTime((prev) => {
          if (prev <= 1) {
            clearInterval(restInterval);
            setIsResting(false);
            setTimer(60);
            return 20;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(restInterval);
    }

    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);

          if (round === 1) {
            setIsResting(true);
            setRound(2);
            setRestTime(20);
            setRevealed(0);
          } else {
            stopBackgroundMusic();
            onGameEndRef.current?.({
              game_type: 'mirror',
              score: scoreRef.current,
              accuracy: accuracyRef.current,
              duration: 120
            });
          }
          return 60;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResting, round]);

  const playBackgroundMusic = () => {
    try {
      musicAudioRef.current = new Audio('https://assets.mixkit.co/music/preview/mixkit-hip-hop-02-738.mp3');
      musicAudioRef.current.loop = true;
      musicAudioRef.current.volume = 0.3;
      musicAudioRef.current.play().catch(e => console.log('Music autoplay blocked:', e));
    } catch (error) {
      console.log('Failed to play music:', error);
    }
  };

  const stopBackgroundMusic = () => {
    if (musicAudioRef.current) {
      musicAudioRef.current.pause();
      musicAudioRef.current = null;
    }
  };

  const playWipeSound = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    const ctx = audioContextRef.current;
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.frequency.value = 400;
    gainNode.gain.value = 0.1;
    
    oscillator.start();
    oscillator.stop(ctx.currentTime + 0.1);
  };

  const handlePoseUpdate = (landmarks) => {
    const now = Date.now();
    if (now - lastWaveTimeRef.current < 200) return;

    if (previousPoseRef.current) {
      const waveResult = detectWave(landmarks, previousPoseRef.current);

      if (waveResult.detected) {
        lastWaveTimeRef.current = now;
        totalMovesRef.current++;
        correctMovesRef.current++;

        const points = Math.floor(waveResult.intensity * 200) + (combo * 8);
        setScore((prev) => prev + points);
        setCombo((prev) => prev + 1);
        setRevealed((prev) => Math.min(prev + 2, 100));
        
        const newAccuracy = (correctMovesRef.current / totalMovesRef.current) * 100;
        setAccuracy(newAccuracy);

        if (waveResult.intensity > 0.1) {
          setFeedback('excellent');
        } else {
          setFeedback('good');
        }
        
        setShowFeedback(true);
        playWipeSound();
        
        setTimeout(() => setShowFeedback(false), 400);
        setTimeout(() => setCombo(0), 2000);
      }
    }

    previousPoseRef.current = landmarks;
  };

  return (
    <div className="relative w-full h-screen bg-black overflow-hidden">
      <WebcamFeed onPoseUpdate={handlePoseUpdate} isActive={!isResting} cameraOpacity={0.3} viewMode={viewMode} />
      
      {isResting ? (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80 backdrop-blur-md">
          <div className="text-center">
            <div className="mb-8">
              <div className="text-6xl mb-4 animate-pulse">✨</div>
              <h2 className="font-heading text-4xl font-black uppercase text-primary mb-2">
                {round === 1 ? 'GET READY!' : 'REST TIME!'}
              </h2>
              <p className="text-muted-foreground font-body text-lg uppercase tracking-wider">
                {round === 1 ? 'Prepare for Mirror Wipe' : 'Round 1 Complete! Get Ready for Round 2'}
              </p>
            </div>
            
            <div className="bg-muted/20 rounded-xl p-8 backdrop-blur-xl border border-primary/30">
              <div className="text-7xl font-heading font-black text-accent mb-2">
                0:{restTime.toString().padStart(2, '0')}
              </div>
              <div className="text-sm text-muted-foreground uppercase tracking-wider">Rest Time</div>
            </div>

            {round === 1 && (
              <div className="mt-8 space-y-3 text-left max-w-md mx-auto">
                <div className="flex items-center gap-3 text-foreground">
                  <span className="text-2xl">👋</span>
                  <span className="font-body">Wave your hands to wipe the mirror</span>
                </div>
                <div className="flex items-center gap-3 text-foreground">
                  <span className="text-2xl">🎯</span>
                  <span className="font-body">Reveal 100% to complete</span>
                </div>
                <div className="flex items-center gap-3 text-foreground">
                  <span className="text-2xl">⚡</span>
                  <span className="font-body">2 rounds × 1 minute each</span>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="absolute top-20 left-1/2 transform -translate-x-1/2 bg-black/60 backdrop-blur-xl border border-accent/50 rounded-lg px-6 py-2 z-30">
            <div className="text-accent font-heading text-lg uppercase tracking-wider">
              ROUND {round} / 2
            </div>
          </div>

          <div className="absolute top-4 left-4 z-30">
            <button
              onClick={() => setViewMode(prev => prev === 'camera' ? 'avatar' : 'camera')}
              className="flex items-center gap-2 bg-black/70 backdrop-blur-xl border border-white/20 rounded-lg px-4 py-2 transition-all duration-200 hover:border-primary/50 hover:bg-black/80 active:scale-95"
            >
              {viewMode === 'avatar' ? (
                <>
                  <User className="w-4 h-4 text-[#00E5FF]" />
                  <span className="text-xs font-heading font-bold uppercase tracking-wider text-[#00E5FF]">Avatar</span>
                </>
              ) : (
                <>
                  <Camera className="w-4 h-4 text-primary" />
                  <span className="text-xs font-heading font-bold uppercase tracking-wider text-primary">Camera</span>
                </>
              )}
            </button>
          </div>

          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative w-96 h-96">
              <div
                className="absolute inset-0 bg-gradient-to-br from-primary via-secondary to-accent rounded-lg"
                style={{
                  opacity: revealed / 100,
                  boxShadow: '0 0 40px rgba(0,240,255,0.5)'
                }}
              >
                <div className="absolute inset-0 flex items-center justify-center text-6xl animate-pulse-neon">
                  🎯
                </div>
              </div>
              
              <div
                className="absolute inset-0 bg-muted/90 backdrop-blur-xl rounded-lg"
                style={{
                  clipPath: `inset(0 0 ${revealed}% 0)`
                }}
                data-testid="mirror-fog"
              >
                <div className="absolute inset-0 flex items-center justify-center text-2xl text-muted-foreground font-heading uppercase tracking-wider">
                  Wave to Reveal
                </div>
              </div>
            </div>
          </div>

          <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 text-center">
            <div className="text-primary font-heading text-lg uppercase tracking-wider">
              REVEALED: {revealed}%
            </div>
          </div>

          <GameHUD score={score} combo={combo} accuracy={accuracy} timer={timer} />
          <FeedbackOverlay feedback={feedback} visible={showFeedback} />
        </>
      )}
    </div>
  );
};

export default MirrorGame;
